import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast, Toaster } from "sonner";
import {
  FileText,
  Upload,
  CheckCircle2,
  Download,
  Mail,
  Clock,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicFiscalPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/public/fiscal/${token}`);
        setCtx(r.data);
        // Seed values from any saved submission, otherwise from defaults.
        const seed = {};
        for (const f of r.data.schema?.fields || []) {
          seed[f.key] = f.default || "";
        }
        if (r.data.submission) {
          setValues({ ...seed, ...r.data.submission });
        } else {
          // Auto-fill date with today if a date field has no default
          for (const f of r.data.schema?.fields || []) {
            if (f.type === "date" && !seed[f.key]) {
              seed[f.key] = new Date().toISOString().slice(0, 10);
            }
          }
          setValues(seed);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setField = (key, val) => setValues((p) => ({ ...p, [key]: val }));

  const validate = () => {
    const missing = [];
    for (const f of ctx?.schema?.fields || []) {
      if (f.required && !String(values[f.key] || "").trim()) {
        missing.push(f.label);
      }
    }
    return missing;
  };

  const submit = async () => {
    const missing = validate();
    if (missing.length) {
      toast.error(`Please fill: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? "…" : ""}`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/public/fiscal/${token}/submit`, { values });
      toast.success("Submitted ✔ — you can now download the PDF, print, sign & stamp.");
      setCtx((c) => ({ ...c, status: "submitted", submission: values }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API_URL}/public/fiscal/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ctx.slug}_${token.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed — submit the form first.");
    }
  };

  const uploadSigned = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API_URL}/public/fiscal/${token}/upload-signed`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Signed copy uploaded ✔ — operations notified.");
      setCtx((c) => ({ ...c, status: "signed", attached_to_pf: r.data.attached_to_pf }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">Link unavailable</h1>
          <p className="text-slate-600 text-sm">{error}</p>
          <p className="text-slate-500 text-xs mt-4">
            Please contact{" "}
            <a className="text-blue-600 underline" href="mailto:operations@europacific.com">
              operations@europacific.com
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  const schema = ctx.schema;
  const isSubmitted = ctx.status === "submitted" || ctx.status === "signed";
  const isSigned = ctx.status === "signed";

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster richColors position="top-center" />

      {/* Brand header */}
      <header className="bg-[#0d4f8b] text-white">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold">europacific</div>
              <div className="text-xs opacity-80">EP Holding d.o.o. / Europacific d.o.o.</div>
            </div>
            <FileText className="w-8 h-8 opacity-80" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 sm:p-8">
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="fiscal-form-title">
            {schema.title}
          </h1>
          <p className="text-slate-600 text-sm mt-1">{schema.subtitle}</p>

          {ctx.pf_reference && (
            <div className="mt-3 inline-flex items-center gap-2 text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full">
              Shipment reference:{" "}
              <span className="font-medium" data-testid="fiscal-pf-ref">
                {ctx.pf_reference}
              </span>
            </div>
          )}

          {/* Status badge */}
          <div className="mt-4 flex items-center gap-3">
            <span
              className={`text-xs px-3 py-1 rounded-full font-medium ${
                isSigned
                  ? "bg-emerald-100 text-emerald-800"
                  : isSubmitted
                  ? "bg-blue-100 text-blue-800"
                  : "bg-amber-100 text-amber-800"
              }`}
              data-testid="fiscal-status-badge"
            >
              {isSigned ? "Signed copy received" : isSubmitted ? "Submitted — pending signature" : "Awaiting your input"}
            </span>
            <span className="text-xs text-slate-400">
              Link valid until {new Date(ctx.expires_at).toLocaleDateString()}
            </span>
          </div>

          <hr className="my-6 border-slate-200" />

          {/* Fields */}
          <div className="space-y-5">
            {schema.fields.map((f) => (
              <div key={f.key}>
                <Label
                  htmlFor={`fld-${f.key}`}
                  className="text-xs uppercase tracking-wide text-slate-500"
                >
                  {f.label}
                  {f.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {f.type === "textarea" ? (
                  <Textarea
                    id={`fld-${f.key}`}
                    data-testid={`fiscal-field-${f.key}`}
                    rows={f.rows || 2}
                    placeholder={f.placeholder}
                    value={values[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    disabled={isSubmitted}
                    className="mt-1"
                  />
                ) : (
                  <Input
                    id={`fld-${f.key}`}
                    data-testid={`fiscal-field-${f.key}`}
                    type={f.type === "date" ? "date" : "text"}
                    placeholder={f.placeholder}
                    value={values[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    disabled={isSubmitted}
                    className="mt-1"
                  />
                )}
                {f.help && <p className="text-xs text-slate-400 mt-1">{f.help}</p>}
              </div>
            ))}
          </div>

          <hr className="my-6 border-slate-200" />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {!isSubmitted && (
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-[#0d4f8b] hover:bg-[#0a3e6e]"
                data-testid="fiscal-submit-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {saving ? "Submitting…" : "Submit form"}
              </Button>
            )}
            {isSubmitted && (
              <Button
                variant="outline"
                onClick={downloadPdf}
                data-testid="fiscal-download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF (to print / sign / stamp)
              </Button>
            )}
            {isSubmitted && (
              <label
                className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border text-sm font-medium cursor-pointer ${
                  uploading
                    ? "bg-slate-200 text-slate-500"
                    : "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                }`}
                data-testid="fiscal-upload-signed-btn"
              >
                <Upload className="w-4 h-4" />
                {uploading ? "Uploading…" : "Upload signed & stamped copy"}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={uploadSigned}
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          {isSigned && (
            <div className="mt-6 p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Signed copy received — thank you.</div>
                <div className="opacity-80 mt-1">
                  Our operations team has been notified
                  {ctx.attached_to_pf ? " and the document has been attached to your shipment." : "."} You can close this page.
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-2">
          <Mail className="w-3 h-3" />
          Questions? <a className="text-blue-600 hover:underline" href="mailto:operations@europacific.com">operations@europacific.com</a>
        </p>
      </main>
    </div>
  );
}
